import React, { useState, useEffect, useCallback, useRef } from 'react';
import { indicatorPlansApi, sectorsApi } from '../api';
import { toast } from 'sonner';
import { extractApiError } from '../utils/dateValidation';
import { Plus, Trash2, Loader2, Download, Upload } from 'lucide-react';

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const MONTH_KEYS = MONTH_LABELS.map((_, i) => String(i + 1));

const emptyValues = () => Object.fromEntries(MONTH_KEYS.map(k => [k, '']));

const normalize = (plan) => ({
  ...plan,
  sector_id: plan.sector_id || '',
  target: plan.target || '',
  monthly_values: { ...emptyValues(), ...Object.fromEntries(Object.entries(plan.monthly_values || {}).map(([k, v]) => [k, v ?? ''])) },
});

/** Textarea que cresce conforme o texto, para o conteúdo aparecer inteiro */
const AutoTextarea = ({ value, className, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return <textarea ref={ref} rows={1} value={value} className={`${className} resize-none overflow-hidden`} {...props} />;
};

let draftCounter = 0;
const newDraft = (year, sectorId) => ({
  id: null,
  draftKey: `draft-${++draftCounter}`,
  year,
  sector_id: sectorId || '',
  indicator: '',
  target: '',
  monthly_values: emptyValues(),
});

/** F-021 - Planejamento Indicadores e Objetivos (planilha anual por setor) */
const IndicatorPlanning = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [filterSector, setFilterSector] = useState('');
  const [sectors, setSectors] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const inFlight = useRef(new Set());
  const fileInput = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    sectorsApi.listActive()
      .then(res => setSectors(res.data || []))
      .catch(() => toast.error('Erro ao carregar setores'));
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { year };
      if (filterSector) params.sector_id = filterSector;
      const res = await indicatorPlansApi.list(params);
      setRows((res.data || []).map(normalize));
    } catch (error) {
      toast.error('Erro ao carregar planejamento');
    } finally {
      setLoading(false);
    }
  }, [year, filterSector]);

  useEffect(() => { load(); }, [load]);

  const rowKey = (row) => row.id || row.draftKey;

  const updateLocal = (key, patch) => {
    setRows(prev => prev.map(r => (rowKey(r) === key ? { ...r, ...patch } : r)));
  };

  const updateMonth = (row, month, value) => {
    updateLocal(rowKey(row), { monthly_values: { ...row.monthly_values, [month]: value } });
  };

  const saveRow = async (row, override = {}) => {
    const current = { ...row, ...override };
    if (!current.indicator.trim()) return; // linha só é gravada após informar o indicador
    const key = rowKey(row);
    if (!current.id && inFlight.current.has(key)) return; // evita criar a mesma linha duas vezes
    const payload = {
      year,
      sector_id: current.sector_id || null,
      indicator: current.indicator,
      target: current.target || null,
      monthly_values: current.monthly_values,
    };
    try {
      inFlight.current.add(key);
      setSavingKey(key);
      const res = current.id
        ? await indicatorPlansApi.update(current.id, payload)
        : await indicatorPlansApi.create(payload);
      setRows(prev => prev.map(r => (rowKey(r) === key ? normalize(res.data) : r)));
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar linha'));
    } finally {
      inFlight.current.delete(key);
      setSavingKey(null);
    }
  };

  const handleSectorChange = (row, value) => {
    updateLocal(rowKey(row), { sector_id: value });
    saveRow(row, { sector_id: value });
  };

  const handleAddRow = () => {
    setRows(prev => [...prev, newDraft(year, filterSector)]);
  };

  const handleDelete = async (row) => {
    if (row.id) {
      if (!window.confirm(`Excluir o indicador "${row.indicator}" do planejamento?`)) return;
      try {
        await indicatorPlansApi.delete(row.id);
        toast.success('Linha excluída!');
      } catch (error) {
        toast.error('Erro ao excluir');
        return;
      }
    }
    setRows(prev => prev.filter(r => rowKey(r) !== rowKey(row)));
  };

  const handleDownload = async () => {
    try {
      setBusy(true);
      const params = { year };
      if (filterSector) params.sector_id = filterSector;
      const res = await indicatorPlansApi.exportXlsx(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `planejamento_indicadores_${year}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar planilha');
    } finally {
      setBusy(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setBusy(true);
      const res = await indicatorPlansApi.importXlsx(year, file);
      const { created, updated, unknown_sectors } = res.data;
      toast.success(`Planilha importada: ${created} nova(s), ${updated} atualizada(s)`);
      if (unknown_sectors?.length) {
        toast.warning(`Setores não encontrados no cadastro: ${unknown_sectors.join(', ')}`);
      }
      load();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao importar planilha'));
    } finally {
      setBusy(false);
    }
  };

  const cellInput = 'input w-full px-3 py-2.5 text-base bg-transparent';

  return (
    <div className="space-y-4" data-testid="indicator-planning">
      {/* Cabeçalho do formulário */}
      <div className="card p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">Planejamento Indicadores e Objetivos</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={filterSector}
            onChange={(e) => setFilterSector(e.target.value)}
            className="input px-3 w-full sm:w-56"
            data-testid="plan-sector-filter"
          >
            <option value="">Todos os setores</option>
            {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="input px-3 w-full sm:w-28"
            data-testid="plan-year"
          >
            {Array.from({ length: 8 }, (_, i) => currentYear - 3 + i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={handleDownload} disabled={busy} className="btn-secondary" title="Baixar planilha (.xlsx)" data-testid="plan-download">
            <Download size={20} />
            Baixar
          </button>
          <button onClick={() => fileInput.current?.click()} disabled={busy} className="btn-secondary" title="Enviar planilha editada (.xlsx)" data-testid="plan-upload">
            <Upload size={20} />
            Importar
          </button>
          <input ref={fileInput} type="file" accept=".xlsx" className="hidden" onChange={handleUpload} />
          <button onClick={handleAddRow} className="btn-primary" data-testid="plan-add-row">
            <Plus size={20} />
            Adicionar linha
          </button>
        </div>
      </div>

      {/* Planilha */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : (
          <div className="table-container overflow-x-auto">
            <table className="table" style={{ minWidth: 2700 }}>
              <thead>
                <tr>
                  <th style={{ minWidth: 220 }}>Setor</th>
                  <th style={{ minWidth: 340 }}>Indicador</th>
                  <th style={{ minWidth: 170 }} className="text-center">Meta</th>
                  {MONTH_LABELS.map(m => (
                    <th key={m} style={{ minWidth: 160 }} className="text-center">{m}</th>
                  ))}
                  <th style={{ width: 48 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={16} className="text-center py-8 text-gray-400">
                      Nenhuma linha para {year}. Clique em "Adicionar linha".
                    </td>
                  </tr>
                )}
                {rows.map(row => {
                  const key = rowKey(row);
                  return (
                    <tr key={key} data-testid={`plan-row-${key}`}>
                      <td>
                        <select
                          value={row.sector_id}
                          onChange={(e) => handleSectorChange(row, e.target.value)}
                          className={cellInput}
                        >
                          <option value="">Selecione o setor</option>
                          {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </td>
                      <td>
                        <AutoTextarea
                          value={row.indicator}
                          onChange={(e) => updateLocal(key, { indicator: e.target.value })}
                          onBlur={() => saveRow(row)}
                          className={cellInput}
                          placeholder="Nome do indicador"
                        />
                      </td>
                      <td>
                        <AutoTextarea
                          value={row.target}
                          onChange={(e) => updateLocal(key, { target: e.target.value })}
                          onBlur={() => saveRow(row)}
                          className={`${cellInput} text-center font-semibold`}
                        />
                      </td>
                      {MONTH_KEYS.map(m => (
                        <td key={m}>
                          <AutoTextarea
                            value={row.monthly_values[m]}
                            onChange={(e) => updateMonth(row, m, e.target.value)}
                            onBlur={() => saveRow(row)}
                            className={`${cellInput} text-center`}
                          />
                        </td>
                      ))}
                      <td className="text-center">
                        {savingKey === key ? (
                          <Loader2 className="animate-spin text-green-500 inline" size={16} />
                        ) : (
                          <button
                            onClick={() => handleDelete(row)}
                            className="p-1 hover:text-red-400 transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-gray-500 text-xs">
        Edite direto nos campos (salva automaticamente ao sair do campo) ou use Baixar → editar no Excel → Importar. A linha só é gravada depois de informar o indicador.
      </p>
    </div>
  );
};

export default IndicatorPlanning;
